import React, { useState, useEffect } from 'react';
import { ChevronRight, Wifi, Clock, CheckCircle, AlertCircle, Power, Activity, Moon } from 'lucide-react';
import DynamicSensorCard from './DynamicSensorCard';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface SensorValue {
  value: number;
  unit: string;
  timestamp?: string;
  status?: string;
  quality?: number | string;
}

interface DeviceData {
  deviceId: string;
  timestamp: string;
  sensors: Record<string, SensorValue>;
  system?: {
    wifi_signal?: number;
    uptime?: number;
  };
  status?: string;
}

interface DeviceCardProps {
  device: DeviceData;
  onExpand?: (deviceId: string) => void;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, onExpand }) => {
  const [timeAgo, setTimeAgo] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [nextUpdateIn, setNextUpdateIn] = useState('');

  // Update countdown timer every second
  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();
      const lastUpdate = new Date(device.timestamp).getTime();
      const elapsed = Math.floor((now - lastUpdate) / 1000);
      setElapsedSeconds(elapsed);
      
      // Format time ago
      if (elapsed < 60) {
        setTimeAgo(`${elapsed} seconde${elapsed !== 1 ? 's' : ''}`);
      } else if (elapsed < 3600) {
        const mins = Math.floor(elapsed / 60);
        const secs = elapsed % 60;
        setTimeAgo(`${mins}m ${secs}s`);
      } else {
        const hours = Math.floor(elapsed / 3600);
        const mins = Math.floor((elapsed % 3600) / 60);
        setTimeAgo(`${hours}h ${mins}m`);
      }
      
      // Calculate next update (15 minutes telemetry interval)
      const nextUpdateSeconds = 900 - (elapsed % 900); // 900 seconds = 15 minutes
      const nextMins = Math.floor(nextUpdateSeconds / 60);
      const nextSecs = nextUpdateSeconds % 60;
      setNextUpdateIn(`${nextMins}m ${nextSecs}s`);
    };

    updateTimer(); // Initial call
    const interval = setInterval(updateTimer, 1000); // Update every second

    return () => clearInterval(interval);
  }, [device.timestamp]);
  const formatUptime = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}j ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getWifiQuality = (signal?: number) => {
    if (!signal) return { text: 'Inconnu', color: 'text-gray-500 dark:text-gray-400' };
    if (signal >= -50) return { text: 'Excellent', color: 'text-green-600 dark:text-green-400' };
    if (signal >= -60) return { text: 'Très bon', color: 'text-green-500 dark:text-green-400' };
    if (signal >= -70) return { text: 'Bon', color: 'text-yellow-500 dark:text-yellow-400' };
    if (signal >= -80) return { text: 'Moyen', color: 'text-orange-500 dark:text-orange-400' };
    return { text: 'Faible', color: 'text-red-500 dark:text-red-400' };
  };

  // Device status logic:
  // - online: explicitly online or last seen < 1 min
  // - sleeping: last seen < 20 min (considering 15 min telemetry interval)
  // - offline: last seen > 20 min
  const getDeviceStatus = () => {
    const minutesAgo = elapsedSeconds / 60;
    
    if (device.status === 'online' || minutesAgo < 1) {
      return { status: 'online', text: 'En ligne', color: 'text-green-500', bgColor: 'bg-green-100 dark:bg-green-900/30', icon: CheckCircle };
    } else if (minutesAgo < 20) {
      return { status: 'sleeping', text: 'Mode veille', color: 'text-blue-500', bgColor: 'bg-blue-100 dark:bg-blue-900/30', icon: Moon };
    } else {
      return { status: 'offline', text: 'Hors ligne', color: 'text-red-500', bgColor: 'bg-red-100 dark:bg-red-900/30', icon: AlertCircle };
    }
  };

  const deviceStatus = getDeviceStatus();
  const StatusIcon = deviceStatus.icon;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      {/* Device Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-bold">{device.deviceId}</h3>
              <StatusIcon className={`h-5 w-5 ${
                deviceStatus.status === 'online' ? 'text-green-300 animate-pulse' : 
                deviceStatus.status === 'sleeping' ? 'text-blue-300' :
                'text-red-300'
              }`} />
            </div>
            <div className="mt-1 space-y-1">
              <div className="flex items-center space-x-2 text-sm text-primary-100">
                <Clock className="h-3 w-3" />
                <span>Dernière mise à jour: il y a {timeAgo}</span>
              </div>
              {deviceStatus.status === 'sleeping' && (
                <div className="flex items-center space-x-2 text-xs text-primary-200">
                  <Activity className="h-3 w-3" />
                  <span>Prochaine télémétrie dans: {nextUpdateIn}</span>
                </div>
              )}
            </div>
          </div>
          {onExpand && (
            <button
              onClick={() => onExpand(device.deviceId)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className={`px-4 py-2 ${deviceStatus.bgColor} border-b border-gray-200 dark:border-gray-700`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Power className={`h-4 w-4 ${deviceStatus.color}`} />
            <span className={`text-sm font-medium ${deviceStatus.color}`}>
              {deviceStatus.text}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-600 dark:text-gray-400">
            {deviceStatus.status === 'sleeping' && (
              <span className="flex items-center space-x-1">
                <Moon className="h-3 w-3" />
                <span>Économie d'énergie</span>
              </span>
            )}
            <span className="flex items-center space-x-1">
              <Clock className="h-3 w-3" />
              <span>{timeAgo}</span>
            </span>
          </div>
        </div>
      </div>

      {/* System Info */}
      {device.system && (
        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {device.system.wifi_signal !== undefined && (
              <div className="flex items-center space-x-2">
                <Wifi className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <p className={`font-medium ${getWifiQuality(device.system.wifi_signal).color}`}>
                    {device.system.wifi_signal} dBm
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {getWifiQuality(device.system.wifi_signal).text}
                  </p>
                </div>
              </div>
            )}
            {device.system.uptime !== undefined && (
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {formatUptime(device.system.uptime)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Temps actif
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next Update Timer for sleeping devices */}
      {deviceStatus.status === 'sleeping' && (
        <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/10 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-pulse" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Prochaine télémétrie
              </span>
            </div>
            <div className="text-sm font-semibold text-blue-800 dark:text-blue-200">
              {nextUpdateIn}
            </div>
          </div>
        </div>
      )}

      {/* Sensors Grid */}
      <div className="p-4">
        {Object.keys(device.sensors).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(device.sensors).map(([sensorName, sensorData]) => (
              <DynamicSensorCard
                key={sensorName}
                sensorName={sensorName}
                sensorData={sensorData}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Aucun capteur détecté</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceCard;
